import React, { useMemo } from "react";
import { Flame, MapTrifold } from "@phosphor-icons/react";

function fmtAcres(n) {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return Math.round(n).toLocaleString();
}

function fmtTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

export default function StatewideFiresPill({ incidents = [], onFlyTo }) {
  const summary = useMemo(() => {
    const wf = incidents.filter((n) => n.type_category === "WF" && n.is_active);
    const totalAcres = wf.reduce((s, n) => s + (n.incident_size || 0), 0);
    const largest = wf.reduce(
      (best, n) =>
        (n.incident_size || 0) > (best?.incident_size || 0) ? n : best,
      null
    );
    const lastUpdated = wf
      .map((n) => n.modified_at)
      .filter(Boolean)
      .sort()
      .slice(-1)[0];
    return { count: wf.length, totalAcres, largest, lastUpdated };
  }, [incidents]);

  return (
    <section
      data-testid="statewide-fires-pill"
      className="border-b border-tac-border bg-gradient-to-b from-tac-primary/8 to-transparent"
    >
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-1.5">
            <Flame size={12} weight="fill" className="text-tac-primary" />
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-text">
              Washington · Active Wildfires
            </span>
          </div>
          {summary.lastUpdated && (
            <span className="text-[10px] font-mono-tac text-tac-faint">
              {fmtTime(summary.lastUpdated)}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div
              data-testid="statewide-fires-count"
              className={`font-display font-black tabular leading-none ${
                summary.count > 0 ? "text-tac-primary" : "text-tac-dim"
              }`}
              style={{ fontSize: "28px" }}
            >
              {summary.count}
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-tac-faint mt-1">
              Fires
            </div>
          </div>
          <div>
            <div
              data-testid="statewide-fires-acres"
              className="font-display font-black tabular leading-none text-tac-text"
              style={{ fontSize: "28px" }}
            >
              {fmtAcres(summary.totalAcres)}
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-tac-faint mt-1">
              Acres burning
            </div>
          </div>
        </div>

        {summary.largest && summary.largest.incident_size > 0 && (
          <button
            type="button"
            data-testid="statewide-largest-fire"
            onClick={() =>
              summary.largest.latitude != null &&
              onFlyTo?.(summary.largest.latitude, summary.largest.longitude)
            }
            className="w-full mt-3 pt-3 border-t border-tac-border/60 flex items-center gap-2 text-left group"
          >
            <MapTrifold size={11} weight="bold" className="text-tac-dim shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[15px] uppercase tracking-wider font-bold text-tac-faint">
                Largest
              </div>
              <div className="text-xs font-medium text-tac-text truncate group-hover:text-tac-primary transition-colors">
                {summary.largest.name}
              </div>
            </div>
            <div className="text-[15px] font-mono-tac tabular text-tac-dim shrink-0">
              {fmtAcres(summary.largest.incident_size)} ac
              {summary.largest.county && ` · ${summary.largest.county} Co.`}
            </div>
          </button>
        )}

        {summary.count === 0 && (
          <div className="text-[15px] font-mono-tac text-tac-faint mt-2">
            No active WA wildfires reported on NIFC right now.
          </div>
        )}
      </div>
    </section>
  );
}
